import { geocodeAddress, haversineDistance } from './geocoding';

// Given the driver's current position and a list of selected loads,
// returns an ORDERED array of stops using a SINGLE nearest-neighbor pass
// across every pickup AND every dropoff together — not grouped by leg.
//
// This replaced an earlier "all pickups first, then all dropoffs" version.
// Real driver feedback showed that's often wrong in practice: a driver
// heading out for a delivery may pass right by another load's pickup
// location on the way, and should grab it then rather than backtrack
// for it later. So now every stop (pickup or dropoff, from any selected
// load) is just whichever one is genuinely closest next, regardless of
// type — a dropoff can come before another load's pickup if it's closer.
//
// The one rule still enforced: a load's OWN dropoff can never be ordered
// before that same load's OWN pickup (you can't deliver a vehicle before
// picking it up). Everything else is pure nearest-neighbor.
//
// Each returned stop looks like:
// { type: 'pickup' | 'dropoff', loadId, address, lat, lng }
export async function buildOrderedRoute(currentPosition, selectedLoads) {
  // Geocode every pickup and dropoff address once, up front.
  const geocodedLoads = await Promise.all(
    selectedLoads.map(async (load) => {
      const pickupCoords = await geocodeAddress(load.origin);
      const dropoffCoords = await geocodeAddress(load.destination);
      return { load, pickupCoords, dropoffCoords };
    })
  );

  // Build the full pool of stops. A load already marked 'picked up'
  // (e.g. updated manually via Load Detail) only contributes its
  // dropoff — its pickup already happened in real life.
  const allStops = [];

  geocodedLoads.forEach(({ load, pickupCoords, dropoffCoords }) => {
    if (pickupCoords && load.load_status === 'awaiting') {
      allStops.push({
        type: 'pickup',
        loadId: load.id_load,
        address: load.origin,
        lat: pickupCoords.lat,
        lng: pickupCoords.lng,
      });
    }
    if (dropoffCoords) {
      allStops.push({
        type: 'dropoff',
        loadId: load.id_load,
        address: load.destination,
        lat: dropoffCoords.lat,
        lng: dropoffCoords.lng,
      });
    }
  });

  return nearestNeighborOrderWithPickupConstraint(currentPosition, allStops);
}

// Nearest-neighbor ordering across ALL stops together, with one
// constraint enforced: a load's dropoff can't be scheduled before
// that same load's pickup has already been placed earlier in the route
// (or unless that load has no pickup in this pool at all, meaning it
// was already picked up before this route started).
function nearestNeighborOrderWithPickupConstraint(startPosition, stops) {
  const remaining = [...stops];
  const ordered = [];
  const pickedUpLoadIds = new Set();

  // Track which load IDs have a pickup stop in this pool at all —
  // if a load's pickup isn't in the pool, its dropoff is free to be
  // scheduled anytime (it was already picked up earlier, outside this route).
  const loadIdsWithPickupInPool = new Set(
    stops.filter((s) => s.type === 'pickup').map((s) => s.loadId)
  );

  let currentPos = startPosition;

  while (remaining.length > 0) {
    // Filter out any dropoff whose matching pickup hasn't been visited yet.
    const eligible = remaining.filter((stop) => {
      if (stop.type === 'pickup') return true;
      const needsPickupFirst = loadIdsWithPickupInPool.has(stop.loadId);
      return !needsPickupFirst || pickedUpLoadIds.has(stop.loadId);
    });

    // Should always have at least one eligible stop (every load's pickup
    // is reachable before its own dropoff), but guard just in case.
    const pool = eligible.length > 0 ? eligible : remaining;

    let nearestIndex = 0;
    let nearestDistance = haversineDistance(currentPos, pool[0]);

    for (let i = 1; i < pool.length; i++) {
      const dist = haversineDistance(currentPos, pool[i]);
      if (dist < nearestDistance) {
        nearestDistance = dist;
        nearestIndex = i;
      }
    }

    const nextStop = pool[nearestIndex];
    const removeIndex = remaining.indexOf(nextStop);
    remaining.splice(removeIndex, 1);

    if (nextStop.type === 'pickup') {
      pickedUpLoadIds.add(nextStop.loadId);
    }

    ordered.push(nextStop);
    currentPos = nextStop;
  }

  return ordered;
}